import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Alert, List, ListItem, ListItemText,
  Switch, FormControlLabel, TextField, MenuItem, IconButton, Grid, Paper,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { layoutAPI, moduleAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';

export const LayoutBuilderPage: React.FC = () => {
  const { moduleKey } = useParams<{ moduleKey: string }>();
  const [mod, setMod] = useState<any>(null);
  const [layout, setLayout] = useState<any>(null);
  const [viewType, setViewType] = useState('form');
  const [message, setMessage] = useState('');
  const [dragIdx, setDragIdx] = useState<{ si: number; fi: number } | null>(null);

  const load = async () => {
    if (!moduleKey) return;
    const [mods, lay] = await Promise.all([
      moduleAPI.getAll(),
      layoutAPI.get(moduleKey, viewType),
    ]);
    setMod(mods.data.find((m: any) => m.key === moduleKey));
    setLayout(lay.data);
  };

  useEffect(() => { load(); }, [moduleKey, viewType]);

  const moveField = (sectionIdx: number, fieldIdx: number, direction: -1 | 1) => {
    const sections = JSON.parse(JSON.stringify(layout.sections));
    const fields = sections[sectionIdx].fields;
    const target = fieldIdx + direction;
    if (target < 0 || target >= fields.length) return;
    [fields[fieldIdx], fields[target]] = [fields[target], fields[fieldIdx]];
    fields.forEach((f: any, i: number) => { f.sortOrder = i; });
    setLayout({ ...layout, sections });
  };

  const onDrop = (sectionIdx: number, targetIdx: number) => {
    if (!dragIdx || dragIdx.si !== sectionIdx) return;
    const sections = JSON.parse(JSON.stringify(layout.sections));
    const fields = sections[sectionIdx].fields;
    const [moved] = fields.splice(dragIdx.fi, 1);
    fields.splice(targetIdx, 0, moved);
    fields.forEach((f: any, i: number) => { f.sortOrder = i; });
    setLayout({ ...layout, sections });
    setDragIdx(null);
  };

  const toggleField = (sectionIdx: number, fieldIdx: number) => {
    const sections = [...layout.sections];
    sections[sectionIdx].fields[fieldIdx].visible = !sections[sectionIdx].fields[fieldIdx].visible;
    setLayout({ ...layout, sections });
  };

  const setWidth = (sectionIdx: number, fieldIdx: number, width: string) => {
    const sections = [...layout.sections];
    sections[sectionIdx].fields[fieldIdx].width = width;
    setLayout({ ...layout, sections });
  };

  const handleSave = async () => {
    await layoutAPI.save(moduleKey!, viewType, { sections: layout.sections });
    setMessage('Layout saved successfully');
  };

  if (!mod || !layout) return null;

  return (
    <Box>
      <PageHeader
        title={`Layout Builder — ${mod.label}`}
        subtitle={getPageSubtitle('layout_builder')}
        icon={<ViewQuiltIcon />}

        action={<Button variant="contained" onClick={handleSave} sx={catchyPrimaryButtonSx}>Save Layout</Button>}
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {['list', 'form', 'detail'].map((v) => (
          <Button key={v} variant={viewType === v ? 'contained' : 'outlined'}
            onClick={() => setViewType(v)} sx={{ textTransform: 'capitalize', fontSize: 13 }}>
            {v} View
          </Button>
        ))}
      </Box>

      {(layout.sections || []).map((section: any, si: number) => (
        <Card key={si} sx={{ mb: 2, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: brandCss.primaryDark, mb: 2 }}>
              {section.title}
            </Typography>
            <List dense>
              {section.fields.map((f: any, fi: number) => {
                const fieldDef = mod.fields.find((fd: any) => fd.key === f.fieldKey);
                const isCore = fieldDef?.isCoreField;
                return (
                  <ListItem
                    key={f.fieldKey}
                    draggable
                    onDragStart={() => setDragIdx({ si, fi })}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(si, fi)}
                    sx={{
                      bgcolor: fi % 2 === 0 ? '#fff' : '#fafbfc',
                      mb: 0.5, borderRadius: `${RADIUS}px`,
                      border: `1px solid ${brandColors.border}`,
                      cursor: 'grab',
                      '&:hover': { bgcolor: brandCss.green50 },
                    }}
                  >
                    <DragIndicatorIcon sx={{ color: brandColors.textMuted, mr: 1, fontSize: 18 }} />
                    <ListItemText
                      primary={<Typography fontSize={13} fontWeight={600}>{fieldDef?.label || f.fieldKey}</Typography>}
                      secondary={`${fieldDef?.type || 'text'}${isCore ? ' · core field' : ''}`}
                    />
                    <TextField select size="small" value={f.width || 'half'} onChange={(e) => setWidth(si, fi, e.target.value)}
                      sx={{ width: 90, mr: 1, '& .MuiOutlinedInput-root': { height: 32, fontSize: 12 } }}>
                      <MenuItem value="full">Full</MenuItem>
                      <MenuItem value="half">Half</MenuItem>
                    </TextField>
                    <FormControlLabel
                      control={<Switch checked={f.visible !== false} onChange={() => toggleField(si, fi)} color="success" size="small" />}
                      label={<Typography fontSize={12}>Show</Typography>}
                    />
                    <IconButton size="small" onClick={() => moveField(si, fi, -1)} disabled={fi === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => moveField(si, fi, 1)} disabled={fi === section.fields.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
